import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import { Box, Divider } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Label } from "@/components/Label";
import {
  ButtonColorOption,
  ButtonVariantOption,
  SubmitButton,
} from "@/components/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { SizingWrapperStyle } from "@/components/Wrapper";
import Button from "@mui/material/Button";
import {
  type CompanyLogoFormData,
  CompanyLogoFormDataSchema,
} from "@/features/accountSettings/types/schemas";
import {
  CompanyInfoGetAPI,
  CompanyInfoUpdateAPI,
} from "@/features/accountSettings/api/PropertySummaryCompanyInfoAPI";
import { ConfirmDialog } from "@/components/Dialog";

export const PropertySummaryCompanyLogo: React.FC = (_props) => {
  const companyInfo = CompanyInfoGetAPI();
  const [_, companyLogoMutate] = CompanyInfoUpdateAPI();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<CompanyLogoFormData>({
    mode: "all",
    defaultValues: {
      file: null,
      url: null,
    },
    resolver: zodResolver(CompanyLogoFormDataSchema),
  });
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // 初期表示時、未編集かつAPIからURLが来ている場合はURLをフォームに反映
    if (!reactHookForm.formState.isDirty && companyInfo?.companyLogoUri) {
      reactHookForm.setValue("url", companyInfo.companyLogoUri);
    }
  }, [companyInfo]);

  useEffect(() => {
    // アンマウント時にObjectURLを解放
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const handleClearButton = (): void => {
    setConfirmDialogOpen(true);
  };

  // 画像ファイルの削除
  const deleteFile = (): void => {
    // 既存のObjectURLがある場合は解放
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    reactHookForm.setValue("file", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    reactHookForm.setValue("url", null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    void onSubmit();
  };

  // ファイルの新規設定、更新
  const setFile = (file: File | null): void => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      objectUrlRef.current = objectUrl;

      reactHookForm.setValue("file", file, {
        shouldDirty: true,
        shouldValidate: true,
      });
      reactHookForm.setValue("url", objectUrl, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  // 画像の設定、更新、削除
  const onSubmit = reactHookForm.handleSubmit(
    async (data: CompanyLogoFormData) => {
      const fd = new FormData();
      fd.append("url", "");
      if (data.file) {
        fd.append("file", data.file);
      }

      const result = await companyLogoMutate.trigger(fd);
      if (result) {
        toast.success("会社ロゴを更新しました");
        if (result.companyLogoUri) {
          // API由来のURLに差し替える（ObjectURLではなくなる）
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
          reactHookForm.setValue("url", result.companyLogoUri, {
            shouldDirty: false,
          });
          // サーバに保存されたのでdirtyをリセット
          reactHookForm.reset({ file: null, url: result.companyLogoUri });
        }
      }
    }
  );

  const previewUrl = reactHookForm.watch("url");

  return (
    <SectionPaper>
      <Box sx={{ display: "flex" }}>
        <SectionTitle>会社ロゴアップロード</SectionTitle>
      </Box>
      <Divider />
      <Box>
        <Box sx={{ m: 2 }}>
          <Label>会社ロゴ</Label>
          <Box sx={{ mb: 2 }}>
            <Box
              height={160}
              border={1}
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fafafa",
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="会社ロゴ"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ) : (
                <Box sx={{ color: "#666" }}>ロゴがありません</Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button component="label" variant="outlined">
              画像を選択
              <input
                type="file"
                accept="image/*"
                multiple={false}
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setFile(file);
                  // 再度同じファイルを選べるようにvalueをクリア
                  e.currentTarget.value = "";
                }}
              />
            </Button>
            {previewUrl && (
              <Button variant="text" color="error" onClick={handleClearButton}>
                画像を削除する
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          mr: 2,
          my: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <SubmitButton
          onClick={(_event, cancelLoading) => {
            onSubmit()
              .then(() => {
                cancelLoading();
              })
              .catch((reason) => {
                cancelLoading();
                console.error(reason);
              });
          }}
          timeout={30 * 1000}
          label={"保存"}
          disabled={!reactHookForm.formState.isDirty}
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
          loadingIndicator={"設定中..."}
        />
      </Box>
      <ConfirmDialog
        open={confirmDialogOpen}
        title="削除の確認"
        message="会社ロゴを削除してもよろしいですか？"
        confirmLabel="削除する"
        cancelLabel="やめる"
        confirmButtonColorOption={ButtonColorOption.Error}
        onConfirm={() => {
          setConfirmDialogOpen(false);
          deleteFile();
        }}
        onCancel={() => {
          setConfirmDialogOpen(false);
        }}
      />
    </SectionPaper>
  );
};
